import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { CustomRingtone, CustomRingtoneError } from '@app/core/models/custom-ringtones.model';
import { SnackBarType } from '@app/core/models/snack-bar-data.model';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { AssetUrlService } from '@app/core/services/asset-url.service';
import { BrandingService } from '@app/core/services/branding.service';
import { CustomRingtonesService } from '@app/core/services/custom-ringtones.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { InputDialogComponent, InputDialogData } from '@app/shared/components/input-dialog/input-dialog.component';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { ButtonType } from '@app/shared/models/dialog-data';
import { FileSizePipe } from '@app/shared/pipes/file-size.pipe';
import { SecondsToMinutesPipe } from '@app/shared/pipes/seconds-to-minutes.pipe';
import { AudioUtil } from '@app/shared/utils/audio.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { saveAs } from 'file-saver-es';
import { firstValueFrom } from 'rxjs';

import { SharedVoicemail } from '../../../voicemail/models/voicemail.models';
import { VoicemailService } from '../../../voicemail/services/voicemail.service';
import {
  customRingtoneValue,
  defaultNotificationRingtones,
  defaultSharedVoicemailRingtone,
  notificationItemMap,
  NotificationItems,
  NotificationKeyBackend,
  NotificationSettingsBackend,
  NotificationType,
  parseCustomRingtoneId,
  sharedVoicemailSoundOptions,
  Tones,
} from '../../models/settings.models';
import { SettingsPage } from '../../models/settings-page';
import { NotificationService } from '../../services/notification.service';
import { SettingsService } from '../../services/settings.service';

interface SharedVoicemailNotificationItem {
  mailbox: SharedVoicemail;
  settings: NotificationSettingsBackend;
}

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FileSizePipe,
    FormsModule,
    TranslateModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    ReactiveFormsModule,
    BrandingColorDirective,
    MatButton,
    SecondsToMinutesPipe,
  ],
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent extends SettingsPage implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private audioUtil?: AudioUtil;
  private objectURL?: string;
  protected formGroup: FormGroup;
  protected notificationItems: NotificationItems[] = [];
  protected sharedVoicemailItems: SharedVoicemailNotificationItem[] = [];
  protected sharedVoicemailFormArray: FormArray;

  protected customRingtones: CustomRingtone[] = [];
  protected customRingtoneOptions: Tones[] = [];
  protected customRingtoneEdit?: { id: number; name: string };
  protected customRingtonePlayingId?: number;
  protected uploading = false;

  private removedTypes: NotificationType[] = [];
  protected readonly sharedVoicemailSoundOptions = sharedVoicemailSoundOptions;
  protected readonly AppFeature = AppFeature;
  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private formBuilder: FormBuilder,
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    protected configService: AppConfigService,
    private assetUrlService: AssetUrlService,
    protected brandingService: BrandingService,
    private voicemailService: VoicemailService,
    protected customRingtonesService: CustomRingtonesService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private translateService: AppTranslateService
  ) {
    super();
    this.formGroup = this.formBuilder.group({
      formItems: this.formBuilder.array([]),
      sharedVoicemailItems: this.formBuilder.array([]),
    });
    this.sharedVoicemailFormArray = this.formGroup.get('sharedVoicemailItems') as FormArray;
  }

  ngOnInit(): void {
    if (this.configService.features[AppFeature.CustomRingtones]) {
      this.customRingtonesService.customRingtones$.pipe(untilDestroyed(this)).subscribe((ringtones) => {
        this.customRingtones = ringtones;
        this.customRingtoneOptions = ringtones.map((ringtone) => ({
          title: ringtone.name,
          ringtone: customRingtoneValue(ringtone.id),
        }));
      });
    }

    if (!this.configService.features[AppFeature.Chat]) {
      this.removedTypes.push(
        NotificationType.Chat,
        NotificationType.GroupMeetingStarted,
        NotificationType.MeetingJoined
      );
    }
    if (!this.configService.features[AppFeature.Fax]) {
      this.removedTypes.push(NotificationType.IncomingFax);
    }
    if (!this.configService.isFeatureEnabled(AppFeature.EnableCalling)) {
      this.removedTypes.push(
        NotificationType.IncomingCall,
        NotificationType.IncomingSecondCall,
        NotificationType.MissedCall,
        NotificationType.IncomingVoicemail,
        NotificationType.DistinctiveRing1,
        NotificationType.DistinctiveRing2,
        NotificationType.DistinctiveRing3
      );
    }
    this.notificationItems = Object.values(notificationItemMap).filter(
      (item) => !this.removedTypes.includes(item.title)
    );
    this.notificationItems.forEach((_item) => {
      this.formItems.push(this.createItem(_item));
    });

    this.sharedVoicemailItems = this.voicemailService.currentSharedVoicemails.map((mailbox) => ({
      mailbox,
      settings: this.configService.getSharedVoicemailNotificationSettings(mailbox.uuid),
    }));

    this.sharedVoicemailItems.forEach((item) => {
      this.sharedVoicemailFormArray.push(this.createSharedVoicemailItem(item.settings));
    });
  }

  ngOnDestroy(): void {
    this.audioUtil?.pause();

    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL);
    }
  }

  protected get formItems(): FormArray {
    return this.formGroup.get('formItems') as FormArray;
  }

  private createItem(item: NotificationItems): FormGroup {
    return this.formBuilder.group({
      enableSound: item.notificationSound,
      enableNotification: item.showNotification,
      enableForegroundNotification: item.showInForeground,
      soundSelect: item.ringtone,
      departmentSelect: item.department,
    });
  }

  private createSharedVoicemailItem(settings: NotificationSettingsBackend): FormGroup {
    return this.formBuilder.group({
      enableSound: settings.notificationSound,
      enableNotification: settings.showNotification,
      enableForegroundNotification: settings.showInForeground,
      soundSelect: settings.ringtone,
    });
  }

  public async save() {
    this.notificationItems.forEach((item, index) => {
      item.ringtone = this.formItems.controls[index].value.soundSelect;
      item.notificationSound = this.formItems.controls[index].value.enableSound;
      item.showNotification = this.formItems.controls[index].value.enableNotification;
      item.showInForeground = this.formItems.controls[index].value.enableForegroundNotification;
      item.department = this.formItems.controls[index].value.departmentSelect;
    });

    const sharedVoicemailSettings: Record<string, NotificationSettingsBackend> = {};
    this.sharedVoicemailItems.forEach((item, index) => {
      const formValue = this.sharedVoicemailFormArray.controls[index].value;
      sharedVoicemailSettings[item.mailbox.uuid] = {
        ringtone: formValue.soundSelect,
        notificationSound: formValue.enableSound,
        showNotification: formValue.enableNotification,
        showInForeground: formValue.enableForegroundNotification,
      };
    });

    await firstValueFrom(
      this.notificationService.updateNotificationSettings([...this.notificationItems], sharedVoicemailSettings)
    );
  }

  public onCheckChange() {
    this.disabled = false;
  }

  public onDepartmentChanged(index: number, option: string | null) {
    this.formItems.controls[index].patchValue({ select: option });
    this.disabled = false;
  }

  protected async onRingtoneChanged(index: number, option: string) {
    this.formItems.controls[index].patchValue({ select: option });
    this.disabled = false;
    await this.play(option);
  }

  protected async onSharedVoicemailRingtoneChanged(index: number, option: string) {
    this.sharedVoicemailFormArray.controls[index].patchValue({ soundSelect: option });
    this.disabled = false;
    await this.play(option);
  }

  protected async play(select: string, e?: MouseEvent) {
    e?.stopPropagation();

    const customId = parseCustomRingtoneId(select);
    if (customId !== null) {
      await this.playRingtone(customId);
      return;
    }

    this.customRingtonePlayingId = undefined;

    this.audioUtil?.pause();
    this.audioUtil = new Audio(this.assetUrlService.getSoundUrl(`${select}.wav`)) as AudioUtil;
    await this.settingsService.playAudio(this.audioUtil);
  }

  protected getItemName(index: number): string {
    return this.notificationItems[index].title === 'SMS'
      ? this.notificationItems[index].title
      : this.notificationItems[index].title.toLowerCase();
  }

  protected openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  protected async addFile(event: Event) {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files.length) {
      const file = target.files.item(0);

      if (file) {
        const name = file.name.substring(0, file.name.lastIndexOf('.'));
        const result = await firstValueFrom(
          this.dialog
            .open<InputDialogComponent, InputDialogData, string | undefined>(InputDialogComponent, {
              data: {
                title: this.translateService.instant('NOTIFICATION_PREFERENCES.NAME_RINGTONE'),
                initialValue: name,
                confirmLabel: this.translateService.instant('NOTIFICATION_PREFERENCES.SAVE'),
              },
            })
            .afterClosed()
        );

        if (result !== undefined) {
          // user confirmed with `result`
          try {
            this.uploading = true;
            await this.customRingtonesService.uploadRingtone(result, file);
          } catch (error) {
            console.error('[NotificationComponent] Upload ringtone error:', error);

            let msg = '';

            if (error === CustomRingtoneError.MaxSizeExceeded) {
              msg = this.translateService.instant('NOTIFICATION_PREFERENCES.MAX_SIZE_EXCEEDED');
            } else if (error === CustomRingtoneError.AudioFileError) {
              msg = this.translateService.instant('NOTIFICATION_PREFERENCES.AUDIO_FILE_ERROR');
            } else {
              msg = this.translateService.instant('NOTIFICATION_PREFERENCES.FILE_UPLOAD_ERROR');
            }

            this.snackbar.open(msg, this.translateService.instant('NOTIFICATION_PREFERENCES.OK'), {
              panelClass: SnackBarType.WARNING,
            });
          } finally {
            this.uploading = false;
          }
        }
      }
    }
    target.value = '';
  }

  protected async playRingtone(id: number) {
    this.audioUtil?.pause();

    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL);
    }

    const blob = await this.customRingtonesService.getRingtoneBlob(id);
    if (blob) {
      this.objectURL = URL.createObjectURL(blob);

      this.audioUtil = new Audio(this.objectURL) as AudioUtil;

      this.audioUtil.addEventListener('loadedmetadata', () => {
        this.customRingtonePlayingId = id;
        this.settingsService.playAudio(this.audioUtil!).catch((error) => {
          console.error('[NotificationComponent] Failed to play media:', error);
          this.customRingtonePlayingId = undefined;
        });
      });

      this.audioUtil.addEventListener('ended', () => {
        this.customRingtonePlayingId = undefined;
      });

      this.audioUtil.addEventListener('error', () => {
        console.error('[NotificationComponent] Failed to load custom ringtone audio');
        this.customRingtonePlayingId = undefined;
      });
    }
  }

  protected pauseRingtone() {
    this.audioUtil?.pause();
    this.customRingtonePlayingId = undefined;
  }

  protected async handleSaveRingtoneName() {
    if (this.customRingtoneEdit) {
      const name = this.customRingtoneEdit.name.trim();
      if (name === '') {
        return;
      }
      try {
        await this.customRingtonesService.updateRingtone(this.customRingtoneEdit.id, name);
      } catch (error) {
        console.error('[NotificationComponent] Error while updating custom ringtone name:', error);
        this.snackbar.open(
          this.translateService.instant('NOTIFICATION_PREFERENCES.UPDATE_ERROR'),
          this.translateService.instant('NOTIFICATION_PREFERENCES.OK'),
          { panelClass: SnackBarType.WARNING }
        );
      }

      this.customRingtoneEdit = undefined;
    }
  }

  protected handleCancelRingtoneName() {
    this.customRingtoneEdit = undefined;
  }

  protected downloadCustomRingtone(ringtone: CustomRingtone) {
    if (ringtone.blob) {
      saveAs(ringtone.blob, `${ringtone.name}.${ringtone.extension}`);
    }
  }

  protected editCustomRingtone(ringtone: CustomRingtone) {
    this.customRingtoneEdit = {
      id: ringtone.id,
      name: ringtone.name,
    };
  }

  protected async deleteCustomRingtone(id: number) {
    const assignedEventNames = this.findRingtoneAssignments(id);

    if (assignedEventNames.length === 0) {
      await this.performDeleteCustomRingtone(id);
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: null,
        icon: null,
        description: this.translateService.instant('NOTIFICATION_PREFERENCES.DELETE_RINGTONE_IN_USE_MESSAGE', {
          events: assignedEventNames.join(', '),
        }),
        rememberId: null,
        buttons: [
          {
            label: this.translateService.instant('NOTIFICATION_PREFERENCES.CANCEL'),
            action: () => undefined,
            type: ButtonType.matFlatButton,
            color: undefined,
          },
          {
            label: this.translateService.instant('NOTIFICATION_PREFERENCES.DELETE'),
            action: () => this.performDeleteCustomRingtone(id),
            type: ButtonType.matRaisedButton,
            color: 'warn',
          },
        ],
      },
    });
  }

  private findRingtoneAssignments(ringtoneId: number): string[] {
    const value = customRingtoneValue(ringtoneId);
    const names: string[] = [];

    this.formItems.controls.forEach((control, index) => {
      if (control.value.soundSelect === value) {
        names.push(this.notificationItems[index].title);
      }
    });

    this.sharedVoicemailFormArray.controls.forEach((control, index) => {
      if (control.value.soundSelect === value) {
        const mailbox = this.sharedVoicemailItems[index].mailbox;
        names.push(mailbox.name || mailbox.user);
      }
    });

    return names;
  }

  private async performDeleteCustomRingtone(id: number) {
    // Reset assigned form controls *before* the delete fires its subject.next.
    // Doing it after leaves mat-select with a destroyed MatOption as its
    // last-known selection and it stops reflecting the patched value.
    this.resetRingtoneAssignments(id);

    try {
      await this.customRingtonesService.deleteRingtone(id);
    } catch (error) {
      console.error('[NotificationComponent] Ringtone delete error:', error);
      this.snackbar.open(
        this.translateService.instant('NOTIFICATION_PREFERENCES.DELETE_ERROR'),
        this.translateService.instant('NOTIFICATION_PREFERENCES.OK'),
        { panelClass: SnackBarType.WARNING }
      );
    }
  }

  private resetRingtoneAssignments(ringtoneId: number) {
    const value = customRingtoneValue(ringtoneId);

    this.formItems.controls.forEach((control, index) => {
      if (control.value.soundSelect !== value) {
        return;
      }
      // defaultNotificationRingtones is a module-load snapshot — AppConfigService.setNotifications
      // mutates defaultNotificationSettings in place, so we can't read from that directly.
      const backendKey =
        NotificationKeyBackend[this.notificationItems[index].title as keyof typeof NotificationKeyBackend];
      const defaultRingtone = defaultNotificationRingtones[backendKey];
      if (defaultRingtone) {
        control.get('soundSelect')?.setValue(defaultRingtone);
        this.disabled = false;
      }
    });

    this.sharedVoicemailFormArray.controls.forEach((control) => {
      if (control.value.soundSelect === value) {
        control.get('soundSelect')?.setValue(defaultSharedVoicemailRingtone);
        this.disabled = false;
      }
    });
  }
}
